<template>
    
    <div v-bind="$attrs">
        
    <v-autocomplete
        v-model="model"
        @change="onChange"
        @blur="onBlur"
        @update:menu="onUpdateMenu"
        :items="localCollection"
        :loading="isLoading"
        :search-input.sync="textSearch"
        :return-object="returnObject"
        :hide-selected="hideSelected"
        :hide-no-data="hideData"
        :item-text="itemText"
        :item-value="itemValue"
        :label="label"
        :clearable="clearable"
        :disabled="disabled"
        :multiple="multiple"
        :placeholder="placeholder"
        :outlined="outlined"
        :dense="dense"
        :error-messages="errorMessages"
        :hide-details="hideDetails"
        :small-chips="smallChips"
        class="custom-placeholder"
        >

        <template v-slot:no-data>
            <v-card flat>
                <v-card-text class="py-0 px-2 text-caption">
                    <span>Buscando por <strong class="indigo--text">{{ textSearch }}</strong>, não foi encontrado.</span>
                </v-card-text>
            </v-card>
            
        </template>

        <template v-slot:append-outer>
            <slot name="append-outer"></slot>
        </template>

        <template v-slot:append>
            
            <v-tooltip bottom v-if="showAdd">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" small icon @click="openDialogCreate">
                        <v-icon btn color="green darken-1">mdi-plus</v-icon>
                    </v-btn>
                </template>
                <span>Novo</span>
            </v-tooltip>

            <slot name="append"></slot>

        </template>

        <!-- Tratando o slot que mostra o texto do campo -->
        <template v-slot:selection="{ item, index }">

            <span v-if="index === 0">{{ item[itemText] }}</span>
            <span v-if="index === 1" class="grey--text caption ml-1">(+{{ model.length - 1 }} outros)</span>

        </template>

    </v-autocomplete>
</div>
</template>

<script setup>

import { watch, onMounted, defineEmits, defineProps, ref, computed, onBeforeUpdate } from 'vue'
import { useStore } from '@/composables/useStore'
import debounce from 'lodash/debounce'

//Events
const emit = defineEmits(['input', 'update:search', 'onShowFormCreate', 'update:loading', 'update:item', 'change', 'blur', 'menu-closed'])

//Props
const props = defineProps({
        //Propriedades de dados
        value: { type: [String, Number, Object, Array] },
        item: { type: [Object, Array], required: false },
        errorMessages: { type: Array, required: false },
        columnOrder: { type: String, default: 'name' },
        columns: { type: String, default: 'id,name' },
        with: { type: String, required: false },

        //Propriedades de eventos
        clearable: { type: Boolean, required: false },
        disabled: { type: Boolean, required: false },
        multiple: { type: Boolean, required: false },

        //Propriedades de configuração
        itemText: { type: String, default: 'name' },
        itemValue: { type: String, default: 'id' },
        label: { type: String, required: false },
        placeholder: { type: String, default: "Comece a digitar para pesquisar"},
        outlined: { type: Boolean, required: false },
        dense: { type: Boolean, required: false },
        hideDetails: { type: [Boolean, String], default: 'auto' },
        smallChips: { type: Boolean, required: false },
        showAdd: { type: Boolean, required: false },
        hideSelected: { type: Boolean, required: false },
        returnObject: { type: Boolean, required: false },
        storeAction: { type: String, required: false },
        filters: { type: Object, required: false },
        loadData: { type: Boolean, default: false },

    })

//Data
const textSearch = ref('')
const isLoading = ref(false)
const hideData = ref(true)

//Vuex
const store = useStore()
const ActionRequest = (params) => store.dispatch(props.storeAction, params)

const model = computed({
    get() {
        return props.value
    },
    set(val) {
        emit('input', val)
    }  
})

const localCollection = ref([])

watch(localCollection, (value) => {

    if(!value) return

    if(value.length === 0) return
    emit('onShowFormCreate', false)
})

watch(() => props.item, (value) => {

    if(value === undefined) return

    if(!Array.isArray(value)) {

        if(value === null) {
            model.value = null
            return
        }
        
        localCollection.value = [ 
            ...localCollection.value, 
            value
         ]    
        
    } else {
        localCollection.value = [ ...value, ...localCollection.value ]
    }
    
})

function getData() {

    const params = {
        search: textSearch.value,
        columns: props.columns,
        column_order: props.columnOrder,
        with: props.with,
        ...props.filters
    }

    isLoading.value = true
    hideData.value = true
    ActionRequest(params)
        .then(res => {

            localCollection.value = [
                ...localCollection.value,
                ...res.data
            ]
        })
        .finally(() => {
            isLoading.value = false

            if(localCollection.value.length === 0 && textSearch.value !== null) {
                hideData.value = false
            }
        })
    }

function openDialogCreate() {
    localCollection.value = []
    model.value = null
    emit('onShowFormCreate', true)
}

function onChange(val) {
    // Emite evento de mudança para o componente pai
    emit('change', val)
    // Também atualiza o item (compatibilidade com listeners que esperam 'update:item')
    emit('update:item', val)
}

function onBlur() {
    emit('blur')
}

function onUpdateMenu(val) {
    // Quando o menu for fechado, dispara um evento específico
    if(!val) {
        emit('menu-closed')
    }
}

const debouncedSearch = debounce((newVal, oldVal) => {

    if(!oldVal) return
    if (oldVal.length === 0) return
    
    getData()
}, 500)

// Watchers
watch(textSearch, (newVal, oldVal) => {
  debouncedSearch(newVal, oldVal)
})

watch(() => props.filters, () => {
    getData()
})

//Define se o carregamento da lista é automático
onMounted(() => {

    //Verifica se existe item
    if(props.item !== undefined && props.item !== null) {

        if(!Array.isArray(props.item)) {
            localCollection.value = [ props.item ]    
        } else {
            localCollection.value.push(...props.item)
        }
    }

    if (props.loadData) {
        getData()
    }

})

onBeforeUpdate(() => {

    if(model.value === null || model.value === undefined) { 
        model.value = null
        
        if(!props.loadData && (props.item === undefined || props.item === null) && !textSearch.value) {
            localCollection.value = []
        }
    }

})


</script>